// Angular imports
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

// Guard imports
import { AuthGuard } from './_guards/auth.guard';
import { AccessGuard } from './_guards/access.guard';
import { CommunityGuard } from './_guards/community.guard';
import { ContextGuard } from './_guards/context.guard';
import { DataGuard } from './_guards/data.guard';

// Component imports
import { SearchComponent } from './search/search.component';
import { ContactUsComponent } from './shared-components/contact-us/contact-us.component';
import { HomeComponent } from './shared-components/home/home.component';
import { TimeExpenseConfirmationComponent } from './time-expense-submission-history-confirmation/components/time-expense-confirmation/time-expense-confirmation.component';
import { WorkQueueActionsComponent } from './work-queue-actions-confirmation/work-queue-actions/work-queue-actions.component';
import { ContactPageComponent } from './shared-components/contact-us/contact-page/contact-page.component';

const appRoutes: Routes = [
    {
        path: 'home',
        component: HomeComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'search',
        data: { anonymousAccess: false, newWindow: true, resizable: false, outletOnly: true },
        component: SearchComponent,
        canActivate: [AuthGuard, AccessGuard]
    },
    {
        path: 'contact-us',
        component: ContactUsComponent,
        canActivate: [AuthGuard]
    },
    {
        path: ':communityId/search',
        component: SearchComponent,
        canActivate: [AuthGuard, AccessGuard, CommunityGuard, ContextGuard]
    },
    {
        path: 'tools',
        loadChildren: () => import('./lotunit-tools-module/lotunit.module').then((m) => m.LotUnitModule),
        canActivate: [AuthGuard]
    },
    {
        // Research and Development Module for Email Templates
        path: 'email-templates',
        data: { anonymousAccess: false, newWindow: true, resizable: false },
        loadChildren: () =>
            import('./email-templates-module/email-templates.module').then((m) => m.EmailTemplatesModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'cirahelp',
        data: { anonymousAccess: true, newWindow: true, resizable: false, outletOnly: true },
        loadChildren: () => import('./help-module/help.module').then((m) => m.HelpModule)
    },
    {
        path: 'ciramail',
        data: { anonymousAccess: false, newWindow: true, resizable: false, outletOnly: true },
        loadChildren: () => import('./ciramail-module/ciramail.module').then((m) => m.CiramailModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'user',
        loadChildren: () => import('./user-module/user.module').then((m) => m.UserModule)
    },
    {
        path: 'TESAction',
        component: TimeExpenseConfirmationComponent
    },
    {
        path: 'workQueueActions',
        component: WorkQueueActionsComponent
    },
    {
        path: 'ciranet-support',
        component: ContactPageComponent,
        data: { anonymousAccess: false, newWindow: true, resizable: false, outletOnly: true },
        canActivate: [AuthGuard, DataGuard]
    },
    {
        path: 'my-work',
        loadChildren: () => import('./mywork/mywork.module').then((m) => m.MyworkModule)
    },
    {
        path: 'workflow',
        loadChildren: () => import('./workflow/workflow.module').then((m) => m.WorkflowModule)
    },
    {
        path: 'community',
        loadChildren: () => import('./community-module/community.module').then((m) => m.CommunityModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'property',
        loadChildren: () => import('./property-module/property.module').then((m) => m.PropertyModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'cb',
        loadChildren: () =>
            import('./cirabooks/community-cirabooks-module/community-cirabook.module').then(
                (m) => m.CommunityCirabooksModule
            ),
        canActivate: [AuthGuard]
    },
    {
        path: 'ccb',
        loadChildren: () =>
            import('./cirabooks/corporate-cirabooks-module/corporate-cirabooks.module').then(
                (m) => m.CorporateCirabooksModule
            ),
        canActivate: [AuthGuard]
    },
    {
        path: 'gcb',
        loadChildren: () =>
            import('./cirabooks/general-cirabooks-module/general-cirabooks.module').then(
                (m) => m.GeneralCirabooksModule
            ),
        canActivate: [AuthGuard]
    },
    {
        path: 'manage-property',
        loadChildren: () =>
            import('./manage-property-module/manage-property.module').then((m) => m.ManagePropertyModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'configuration',
        loadChildren: () => import('./configuration-module/configuration.module').then((m) => m.ConfigurationModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'configurations',
        loadChildren: () =>
            import('./configurations-module/configurations-routing.module').then((m) => m.ConfigurationsRoutingModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'my-portfolio',
        loadChildren: () => import('./user-portfolio-module/user-portfolio.module').then((m) => m.UserPortfolioModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'community-properties',
        loadChildren: () =>
            import('./community-property-workspace-module/community-property-workspace.module').then(
                (m) => m.CommunityPropertyWorkspaceModule
            ),
        canActivate: [AuthGuard]
    },
    {
        path: 'reports',
        loadChildren: () => import('./reports-module/reports.module').then((m) => m.ReportsModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'cira-view-email',
        data: { anonymousAccess: true, newWindow: true, resizable: false, outletOnly: true },
        loadChildren: () => import('./email-viewer-module/email-viewer.module').then((m) => m.EmailViewerModule)
    },
    {
        path: 'calendar',
        data: { anonymousAccess: false, newWindow: true, resizable: false, outletOnly: true },
        loadChildren: () => import('./my-calendar-module/my-calendar.module').then((m) => m.MyCalendardModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'violations-configurations',
        loadChildren: () =>
            import('./violations-configuration-module/violations-configuration.module').then(
                (m) => m.ViolationsConfigurationModule
            ),
        canActivate: [AuthGuard]
    },
    {
        path: 'closing-portal-configurations',
        loadChildren: () =>
            import('./closing-portal-configuration-module/closing-portal-configuration.module').then(
                (m) => m.ClosingPortalConfigurationModule
            ),
        canActivate: [AuthGuard]
    },
    {
        path: 'community-configurations',
        loadChildren: () =>
            import('./community-configuration-module/community-configuration.module').then(
                (m) => m.CommunityConfigurationModule
            ),
        canActivate: [AuthGuard]
    },
    {
        path: 'collections-configurations',
        loadChildren: () =>
            import('./collections-configuration-module/collections-configuration.module').then(
                (m) => m.CollectionsConfigurationModule
            ),
        canActivate: [AuthGuard]
    },
    {
        path: 'ca-accounting',
        loadChildren: () => import('./ca-accounting-module/ca-accounting.module').then((m) => m.CAAccountingModule),
        canActivate: [AuthGuard]
    },
    { path: '**', pathMatch: 'full', redirectTo: 'home' }
];

@NgModule({
    imports: [RouterModule.forRoot(appRoutes, { enableTracing: false })],
    exports: [RouterModule]
})
export class AppRoutingModule {}
